import { FC, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion, Variant } from 'framer-motion';

import config from 'config';
import { copyToClipBoard } from 'common/copyToClipboard';
import Button from 'components/_core/Button';
import SunshineIcon from 'components/_core/SunshineIcon';
import { logEvent } from 'features/Analytics/analytics';

import locales from './locales';

interface ReferralCopyButtonProps {
  promoCode: string;
}

type CopyButtonState = 'idle' | 'clicked';

let timer: NodeJS.Timeout | null = null;

const variants = {
  clicked: { opacity: 1, transition: { duration: 0.3, ease: 'easeOut' }, y: 0 },
  idle: (direction: number) => ({
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
    y: direction * 10,
  }),
} satisfies Record<CopyButtonState, Variant>;

const ReferralCopyButton: FC<ReferralCopyButtonProps> = ({ promoCode }) => {
  const [state, setState] = useState<CopyButtonState>('idle');
  const direction = state === 'clicked' ? 1 : -1;

  const onCopyLink = () => {
    const link = `${config.publicWebsiteUrl}/r/${promoCode}`;
    copyToClipBoard(link);
    setState('clicked');
    logEvent({
      name: 'Referral Copy Code Button Clicked',
      usePrefix: false,
    });
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => setState('idle'), 1500);
  };

  return (
    <Button onClick={onCopyLink} variant="inline-secondary">
      <Box height="100%" overflow="hidden" width="100%">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            animate="clicked"
            custom={direction}
            exit="idle"
            initial="idle"
            key={state}
            variants={variants}
          >
            {state === 'idle' ? (
              locales.copyLink
            ) : (
              <>
                <SunshineIcon name="tick" /> {locales.copied}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Button>
  );
};

export default ReferralCopyButton;
